import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { FaUserPlus } from "react-icons/fa";
import "../styles/practitionerList.css";
import { useLocation, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import InviteModal from '../components/InviteModal';

function PractitionerList() {
  const location = useLocation();
  const [startIndex, setStartIndex] = useState(0);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [practitioners, setPractitioners] = useState(
    location.state?.practitioners || []
  );
  const userLocation = location.state?.userLocation || {
    latitude: null,
    longitude: null,
  };

  const [filters, setFilters] = useState({
    specialistType: "All",
    distance: "15 Miles",
    gender: "All",
  });

  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteDoctor, setInviteDoctor] = useState(null);

  const applyFilters = async () => {
    try {
      const queryParams = new URLSearchParams({
        latitude: userLocation.latitude || 24.9288578,
        longitude: userLocation.longitude || 67.0345444,
        max_distance: filters.distance.replace(" Miles", ""), // strip " Miles"
        speciality:
          filters.specialistType === "All" ? "" : filters.specialistType,
        gender: filters.gender === "All" ? "" : filters.gender,
      });

      const response = await fetch(
        `https://portal.virtualtriage.ca/api/v1/doctor/filtered-nearby-doctors/?${queryParams}`
      );
      const data = await response.json();

      if (response.ok && data.status && Array.isArray(data.data)) {
        setPractitioners(data.data);
        setStartIndex(data.data.length); // reset index if needed
      } else {
        setPractitioners([]);
      }
    } catch (error) {
      console.error("Error applying filters:", error);
    }
  };

  const loadMorePractitioners = async () => {
    try {
      const response = await fetch(
        `https://portal.virtualtriage.ca/api/v1/doctor/search-doctors/?search=uk&startindex=${startIndex}`
      );
      if (!response.ok) {
        throw new Error("Failed to load more practitioners");
      }
      const data = await response.json();
      setPractitioners((prevPractitioners) => [
        ...prevPractitioners,
        ...data.data,
      ]);
      setStartIndex((prevIndex) => prevIndex + data.data.length);
    } catch (error) {
      console.error("Error loading more practitioners:", error);
    }
  };

  return (
    <div className="practitioner-list-page">
      <Navbar />
      <div className="practitioner-list-container">
        {/* Mobile-only Filter Toggle Button */}
        <button
          className="mobile-filter-toggle"
          onClick={() => setShowMobileFilters(true)}
        >
          Show Filters
        </button>

        {/* Mobile Overlay */}
        {showMobileFilters && (
          <div
            className="mobile-overlay"
            onClick={() => setShowMobileFilters(false)}
          ></div>
        )}

        {/* Sidebar (Desktop and Mobile) */}
        <div className={`sidebar ${showMobileFilters ? "show" : ""}`}>
          <div className="filter-group">
            <label>Specialist type:</label>
            <select
              value={filters.specialistType}
              onChange={(e) =>
                setFilters({ ...filters, specialistType: e.target.value })
              }
            >
              <option>All</option>
              <option>Dermatologist</option>
              <option>Physiotherapist</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Distance:</label>
            <select
              value={filters.distance}
              onChange={(e) =>
                setFilters({ ...filters, distance: e.target.value })
              }
            >
              <option>5 Miles</option>
              <option>10 Miles</option>
              <option>15 Miles</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Gender:</label>
            <select
              value={filters.gender}
              onChange={(e) =>
                setFilters({ ...filters, gender: e.target.value })
              }
            >
              <option>All</option>
              <option>Male</option>
              <option>Female</option>
            </select>
          </div>
          <div className="filter-group">
            <button className="apply-filters-button" onClick={applyFilters}>
              Apply Filters
            </button>
          </div>
        </div>

        <div className="practitioner-list-results-container">
          <main className="practitioner-list">
            <h2>Practitioner Results</h2>
            {Array.isArray(practitioners) && practitioners.length > 0 ? (
              <div className="practitioner-list">
                <ul className="practitioner-lists">
                  {practitioners.map((pract) => (
                    <li key={pract._id} className={pract.verified ? "verified" : "unverified"}>
                      <div className="practitioner-info">
                        <div>
                          <h3>
                            {pract.full_name}
                            {pract.verified && (
                              <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#00c853", marginLeft: 8 }} title="Verified Practitioner" />
                            )}
                          </h3>
                          {pract.experience_years && (
                            <p>
                              <strong>Experience:</strong> {pract.experience_years} years
                            </p>
                          )}
                          {pract.subspecialties &&
                            (Array.isArray(pract.subspecialties) ? (
                              pract.subspecialties.length > 0 && (
                                <p>
                                  <strong>Subspecialties:</strong> {pract.subspecialties.join(", ")}
                                </p>
                              )
                            ) : (
                              <p>
                                <strong>Subspecialties:</strong> {pract.subspecialties}
                              </p>
                            ))}
                          {pract.areas_of_expertise &&
                            (Array.isArray(pract.areas_of_expertise) ? (
                              pract.areas_of_expertise.length > 0 && (
                                <p>
                                  <strong>Areas of Expertise:</strong> {pract.areas_of_expertise.join(", ")}
                                </p>
                              )
                            ) : (
                              <p>
                                <strong>Areas of Expertise:</strong> {pract.areas_of_expertise}
                              </p>
                            ))}
                          {pract.diseases_and_treatments &&
                            (Array.isArray(pract.diseases_and_treatments) ? (
                              pract.diseases_and_treatments.length > 0 && (
                                <p>
                                  <strong>Diseases & Treatments:</strong> {pract.diseases_and_treatments.join(", ")}
                                </p>
                              )
                            ) : (
                              <p>
                                <strong>Diseases & Treatments:</strong> {pract.diseases_and_treatments}
                              </p>
                            ))}
                          {pract.conditions_and_treatments &&
                            (Array.isArray(pract.conditions_and_treatments) ? (
                              pract.conditions_and_treatments.length > 0 && (
                                <p>
                                  <strong>Conditions & Treatments:</strong> {pract.conditions_and_treatments.join(", ")}
                                </p>
                              )
                            ) : (
                              <p>
                                <strong>Conditions & Treatments:</strong> {pract.conditions_and_treatments}
                              </p>
                            ))}
                          {pract.rating && (
                            <p>
                              <strong>Rating:</strong> {pract.rating} ⭐
                            </p>
                          )}
                          {pract.locations && pract.locations.length > 0 && (
                            <p>
                              <strong>Location:</strong>{" "}
                              {[
                                pract.locations[0].address,
                                pract.locations[0].city,
                                pract.locations[0].state,
                                pract.locations[0].country,
                                pract.locations[0].postal_code,
                              ]
                                .filter(Boolean)
                                .join(", ")}
                            </p>
                          )}
                        </div>
                      </div>
                      <div style={{ marginTop: "12px" }}>
                        <Link
                          to={`/profile/${pract._id}`}
                          state={{
                            practitioner: pract,
                            schedule: pract.schedule || null,
                          }}
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          <button className="contact-button">View Profile</button>
                        </Link>
                        {pract.status !== false ? (
                          <button className="contact-button" style={{ marginLeft: 8 }}>Book Appointment</button>
                        ) : (
                          <button className="contact-button" style={{ marginLeft: 8 }} onClick={() => { setInviteDoctor(pract); setInviteModalOpen(true); }}><FaUserPlus style={{marginRight: 8}} />Invite</button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
                <button
                  className="load-more-button"
                  onClick={loadMorePractitioners}
                >
                  Load More
                </button>
              </div>
            ) : (
              <p>No practitioners found.</p>
            )}
          </main>
        </div>
      </div>
      <Footer />
      <InviteModal isOpen={inviteModalOpen} onClose={() => setInviteModalOpen(false)} doctorName={inviteDoctor?.full_name} doctorEmail={inviteDoctor?.email} />
    </div>
  );
}

export default PractitionerList;
